import type { NextPage } from "next";
import Template from "@app/Template/Template";
import { PROJECT_NAME } from "@shared/config/GlobalText";
import { configApi } from "@shared/lib/configApi";
import { RouterConfig } from "@shared/lib/routerConfig";
import { Container } from "@shared/ui/container";
import { CustomHead, DEFAULT_OG_IMAGE } from "@shared/ui/CustomHead";
import classes from "./privacy-policy.module.scss";

const PrivacyPolicy: NextPage = () => {
    const title = `Политика конфиденциальности - ${PROJECT_NAME}`;
    const description = `Политика конфиденциальности сайта ${PROJECT_NAME}`;
    const canonicalUrl = `${configApi.API_PUBLIC_DOMAIN}${RouterConfig.PRIVACY_POLICY}`;

    return (
        <Template>
            <CustomHead
                title={title}
                description={description}
                robots={"index,follow"}
                canonical={canonicalUrl}
                openGraph={{
                    title: title,
                    description: description,
                    url: canonicalUrl,
                    type: "website",
                    image: {
                        url: DEFAULT_OG_IMAGE,
                        alt: title
                    }
                }}
            />
            <Container>
                <div className={classes["privacy-policy"]}>
                    <h1 className={classes["privacy-policy__title"]}>
                        Политика конфиденциальности
                    </h1>
                    <p>
                        Настоящая Политика конфиденциальности персональных данных (далее – Политика
                        конфиденциальности) действует в отношении всей информации, которую Общество
                        с ограниченной ответственностью «Гонзо» может получить о Пользователе во
                        время использования сайта{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>
                        , в том числе, но не ограничиваясь путем регистрации на Сайте (создание
                        аккаунта с внесением данных в форму регистрации), создание своего объявления
                        с внесением соответствующих данных о Пользователе и его транспортном
                        средстве.
                    </p>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        1. ОПРЕДЕЛЕНИЕ ТЕРМИНОВ:
                    </h2>
                    <p>
                        1.1. В настоящей Политике конфиденциальности используются следующие термины:
                    </p>
                    <p>
                        1.2. Оператор, осуществляющий обработку персональных данных – Общество с
                        ограниченной ответственностью «Гонзо» (ИНН: 9731042980; КПП: 773101001;
                        ОГРН: 1197746350240), юридический адрес: 121354, Москва г, Можайское ш, дом
                        34А, этаж 2, помещение I (далее по тексту – Компания);
                    </p>
                    <p>
                        1.3. «Персональные данные» - любая информация, относящаяся к прямо или
                        косвенно определенному или определяемому физическому лицу (субъекту
                        персональных данных).
                    </p>
                    <p>
                        1.4. «Обработка персональных данных» - любое действие (операция) или
                        совокупность действий (операций), совершаемых с персональными данными,
                        включая сбор, запись, систематизацию, накопление, хранение, уточнение
                        (обновление, изменение), извлечение, использование, передачу
                        (распространение, предоставление, доступ), обезличивание, блокирование,
                        удаление, уничтожение персональных данных.
                    </p>
                    <p>
                        1.5. «Конфиденциальность персональных данных» - обязательное для соблюдения
                        Оператором или иным получившим доступ к персональным данным лицом требование
                        не допускать их распространения без согласия субъекта персональных данных
                        или наличия иного законного основания.
                    </p>
                    <p>
                        1.6. «Пользователь Сайта (далее – Пользователь)» – лицо, имеющее доступ к
                        Сайту посредством сети Интернет и использующее Сайт.
                    </p>
                    <p>
                        1.7. «Файлы cookie» – текстовые файлы небольшого размера, которые
                        сохраняются на вашем устройстве (персональном компьютере, ноутбуке,
                        планшете, мобильном телефоне и т.п.), при посещении сайтов в сети
                        «Интернет».
                    </p>
                    <h2 className={classes["privacy-policy__subtitle"]}>2. ОБЩИЕ ПОЛОЖЕНИЯ:</h2>
                    <p>
                        2.1. Использование Пользователем Сайта{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>{" "}
                        (далее по тексту – Сайт) означает согласие с настоящей Политикой
                        конфиденциальности и условиями обработки персональных данных Пользователя.
                    </p>
                    <p>
                        2.2. В случае несогласия с условиями Политики конфиденциальности
                        Пользователь должен прекратить использование Сайта.
                    </p>
                    <p>
                        2.3. Настоящая Политика конфиденциальности применяется только к Сайту.
                        Компания не контролирует и не несет ответственность за сайты третьих лиц, на
                        которые Пользователь может перейти по ссылкам, доступным на данном сайте.
                    </p>
                    <p>
                        2.4. Компания не проверяет достоверность персональных данных,
                        предоставляемых Пользователем Сайта.
                    </p>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        3. ПРЕДМЕТ ПОЛИТИКИ КОНФИДЕНЦИАЛЬНОСТИ:
                    </h2>
                    <p>
                        3.1. Настоящая Политика конфиденциальности устанавливает обязательства
                        Компании по неразглашению и обеспечению режима защиты конфиденциальности
                        персональных данных, которые Пользователь предоставляет при использовании
                        сайта{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>
                        .
                    </p>
                    <p>
                        3.2. Персональные данные, разрешённые к обработке в рамках настоящей
                        Политики конфиденциальности, предоставляются Пользователем путём заполнения
                        регистрационной формы на Сайте и включают в себя следующую информацию:
                    </p>
                    <p>3.2.1. Имя и фамилию Пользователя;</p>
                    <p>3.2.2. Номер телефона Пользователя;</p>
                    <p>3.2.3. Адрес электронной почты (e-mail);</p>
                    <p>3.2.4. Телефон пользователя;</p>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        4. ЦЕЛИ СБОРА ПЕРСОНАЛЬНОЙ ИНФОРМАЦИИ ПОЛЬЗОВАТЕЛЯ:
                    </h2>
                    <p>
                        4.1. Персональные данные Пользователя Компания может использовать в целях:
                    </p>
                    <p>4.2. Идентификации Пользователя, зарегистрированного на Сайте,</p>
                    <p>
                        4.3. Предоставления Пользователю доступа к персонализированным ресурсам
                        Сайта,
                    </p>
                    <p>
                        4.4. Установления с Пользователем обратной связи, включая направление
                        уведомлений, запросов, касающихся использования Сайта, оказания услуг,
                        обработка запросов и заявок от Пользователя.
                    </p>
                    <p>
                        4.5. Определения места нахождения Пользователя для обеспечения безопасности,
                        предотвращения мошенничества.
                    </p>
                    <p>
                        4.6. Подтверждения достоверности и полноты персональных данных,
                        предоставленных Пользователем.
                    </p>
                    <p>4.7. Формирования на Сайте объявления Пользователя.</p>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        5. СПОСОБЫ И СРОКИ ОБРАБОТКИ ПЕРСОНАЛЬНОЙ ИНФОРМАЦИИ:
                    </h2>
                    <p>
                        5.1. Обработка персональных данных Пользователя осуществляется на период
                        реализации целей использования персональных данных, предусмотренных в
                        разделе 4 «Политики конфиденциальности» в соответствии с требованиями
                        Федерального закона от 27.07.2006 N 152-ФЗ «О персональных данных».
                    </p>
                    <p>
                        5.2. Для отзыва согласия на обработку персональных данных необходимо подать
                        соответствующее заявление в письменной форме по месту нахождения Компании.
                        Компания обязана в течении 10 рабочих дней с момента получения указанного
                        заявления об отзыве прекратить обработку данных о нем.
                    </p>
                    <p>
                        5.3. Персональные данные Пользователя могут быть переданы уполномоченным
                        органам государственной власти Российской Федерации только по основаниям и в
                        порядке, установленным законодательством Российской Федерации.
                    </p>
                    <p>
                        5.4. При утрате или разглашении персональных данных Компания информирует
                        Пользователя об утрате или разглашении персональных данных.
                    </p>
                    <p>
                        5.5. Компания принимает необходимые организационные и технические меры для
                        защиты персональной информации Пользователя от неправомерного или случайного
                        доступа, уничтожения, изменения, блокирования, копирования, распространения,
                        а также от иных неправомерных действий третьих лиц.
                    </p>
                    <p>
                        5.6. Компания совместно с Пользователем принимает все необходимые меры по
                        предотвращению убытков или иных отрицательных последствий, вызванных утратой
                        или разглашением персональных данных Пользователя.
                    </p>
                    <p>
                        5.7. Обрабатываемые персональные данные подлежат уничтожению или
                        обезличиванию по достижению целей обработки или в случае утраты
                        необходимости в достижении этих целей, если иное не предусмотрено
                        федеральным законом.
                    </p>
                    <h2 className={classes["privacy-policy__subtitle"]}>6. ОБЯЗАТЕЛЬСТВА СТОРОН</h2>
                    <p>6.1. Пользователь обязан:</p>
                    <p>
                        6.1.1. Предоставить информацию о персональных данных, необходимую для
                        пользования Сайтом.
                    </p>
                    <p>
                        6.1.2. Обновить, дополнить предоставленную информацию о персональных данных
                        в случае изменения данной информации.
                    </p>
                    <p>6.2. Компания обязана:</p>
                    <p>
                        6.2.1. Использовать полученную информацию исключительно для целей, указанных
                        в п.4 настоящей Политики конфиденциальности и в соответствии с требованием
                        законодательства о персональных данных.
                    </p>
                    <p>
                        6.2.2. Обеспечить хранение конфиденциальной информации в тайне, не
                        разглашать без предварительного письменного разрешения Пользователя, а также
                        не осуществлять продажу, обмен, опубликование, либо разглашение иными
                        возможными способами переданных персональных данных Пользователя, за
                        исключением п.п. 5.3. настоящей Политики Конфиденциальности.
                    </p>
                    <p>
                        6.2.3. Принимать меры предосторожности для защиты конфиденциальности
                        персональных данных Пользователя согласно порядку, обычно используемого для
                        защиты такого рода информации в существующем деловом обороте.
                    </p>
                    <p>
                        6.2.4. Осуществить блокирование персональных данных, относящихся к
                        соответствующему Пользователю, с момента обращения или запроса Пользователя
                        или его законного представителя либо уполномоченного органа по защите прав
                        субъектов персональных данных на период проверки, в случае выявления
                        недостоверных персональных данных или неправомерных действий.
                    </p>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        7. ОТВЕТСТВЕННОСТЬ СТОРОН
                    </h2>
                    <p>
                        7.1. Администрация Сайта, не исполнившая свои обязательства, несёт
                        ответственность за убытки, понесённые Пользователем в связи с неправомерным
                        использованием персональных данных, в соответствии с законодательством
                        Российской Федерации, за исключением случаев, предусмотренных п.п. 5.3. и
                        7.2. настоящей Политики Конфиденциальности.
                    </p>
                    <p>
                        7.2. В случае утраты или разглашения конфиденциальной информации Компания не
                        несёт ответственность, если данная конфиденциальная информация:
                    </p>
                    <p>7.2.1. Стала публичным достоянием до её утраты или разглашения.</p>
                    <p>
                        7.2.2. Была получена от третьей стороны до момента её получения
                        Администрацией Сайта.
                    </p>
                    <p>7.2.3. Была разглашена с согласия Пользователя.</p>
                    <h2 className={classes["privacy-policy__subtitle"]}>8. РАЗРЕШЕНИЕ СПОРОВ</h2>
                    <p>
                        8.1. До обращения в суд с иском по спорам, возникающим из отношений между
                        Пользователем Сайта и Компанией, обязательным является предъявление
                        претензии (письменного предложения о добровольном урегулировании спора).
                    </p>
                    <p>
                        8.2. Получатель претензии в течение 30 календарных дней со дня получения
                        претензии, письменно уведомляет заявителя претензии о результатах
                        рассмотрения претензии.
                    </p>
                    <p>
                        8.3. При недостижении соглашения спор будет передан на рассмотрение в
                        судебный орган в соответствии с действующим законодательством Российской
                        Федерации.
                    </p>
                    <p>
                        8.4. К настоящей Политике конфиденциальности и отношениям между
                        Пользователем и Администрацией Сайта применяется действующее
                        законодательство Российской Федерации.
                    </p>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        9. ПОЛИТИКА ОБРАБОТКИ ФАЙЛОВ COOKIE
                    </h2>
                    <p>
                        9.1. Посещая сайт ООО «Гонзо»{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>{" "}
                        в информационно-телекоммуникационной сети «Интернет», вы соглашаетесь с тем,
                        что ООО «Гонзо» может использовать файлы cookie и иные данные для их
                        последующей обработки системами Яндекс.Метрика metrika.yandex.ru, а также
                        может передавать их третьим лицам для проведения исследований, выполнения
                        работ или оказания услуг. Кроме того, при посещении сайта ООО «Гонзо»{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>{" "}
                        в информационно-телекоммуникационной сети «Интернет» происходит
                        автоматический сбор иных данных, в том числе: технических характеристик
                        устройства, IP-адреса, информации об используемом браузере и языке, даты и
                        времени доступа к сайту, адресов запрашиваемых страниц сайта и иной подобной
                        информации.
                    </p>
                    <p>9.2. Виды Cookies, используемые Компанией:</p>
                    <p>
                        9.2.1. В зависимости от используемых вами браузера и устройства используются
                        разные наборы файлов cookie, включающие в себя строго необходимые,
                        эксплуатационные, функциональные и аналитические файлы cookie.
                    </p>
                    <p>
                        9.3. Используемые вами браузер и (или) устройство могут позволять вам
                        блокировать, удалять или иным образом ограничивать использование файлов
                        cookie. Но файлы cookie являются важной частью сайта ООО «Гонзо»{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>{" "}
                        в информационно-телекоммуникационной сети «Интернет», поэтому блокировка,
                        удаление или ограничение их использования может привести к тому, что вы
                        будете иметь доступ не ко всем функциям сайта.
                    </p>
                    <p>9.4. Реквизиты Администратора сайта</p>
                    <div className={classes.table}>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Полное фирменное наименование на русском языке
                            </div>
                            <div className={classes.table__cell}>
                                Общество с ограниченной ответственностью «Гонзо»
                            </div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Сокращенное фирменное наименование на русском языке
                            </div>
                            <div className={classes.table__cell}>ООО «Гонзо»</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>Юридический адрес</div>
                            <div className={classes.table__cell}>
                                121354, город Москва, Можайское ш., д. 34а, этаж 2 помещение I{" "}
                            </div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Адрес для отправки почтовой корреспонденции
                            </div>
                            <div className={classes.table__cell}>
                                121354, город Москва, Можайское ш., д. 34а, этаж 2 помещение I
                            </div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Адрес электронной почты – официальный, совпадающий с данными в ЕРССД
                                / указанный при получении лицензировании.
                            </div>
                            <div className={classes.table__cell}>
                                <a
                                    className={classes["privacy-policy__link"]}
                                    href="https://likvi.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    https://likvi.com/
                                </a>
                            </div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Дата государственной регистрации
                            </div>
                            <div className={classes.table__cell}>30.05.2019</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>Регистрационный номер (ОГРН)</div>
                            <div className={classes.table__cell}>1197746350240</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Наименование органа, осуществившего государственную регистрацию
                            </div>
                            <div className={classes.table__cell}>
                                Межрайонная инспекция Федеральной налоговой службы №31 по г. Москве
                            </div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Идентификационный номер налогоплательщика (ИНН)
                            </div>
                            <div className={classes.table__cell}>9731042980</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Код причины постановки на учет (КПП)
                            </div>
                            <div className={classes.table__cell}>773101001</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>
                                Наименование и номер налогового органа, осуществившего постановку
                                юридического лица на учет
                            </div>
                            <div className={classes.table__cell}>ИФНС России № 31 по г. Москве</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes["table__cell--full"]}>
                                СВЕДЕНИЯ О РУКОВОДИТЕЛЕ (ЕДИНОЛИЧНОМ ИСПОЛНИТЕЛЬНОМ ОРГАНЕ)
                            </div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>Должность</div>
                            <div className={classes.table__cell}>Генеральный директор</div>
                        </div>
                        <div className={classes.table__row}>
                            <div className={classes.table__cell}>Ф. И. О.</div>
                            <div className={classes.table__cell}>Трифаленков Александр Ильич</div>
                        </div>
                    </div>
                    <h2 className={classes["privacy-policy__subtitle"]}>
                        10. ДОПОЛНИТЕЛЬНЫЕ УСЛОВИЯ
                    </h2>
                    <p>
                        10.1. Компания вправе вносить изменения в настоящую Политику
                        конфиденциальности без согласия Пользователя.
                    </p>
                    <p>
                        10.2. Новая Политика конфиденциальности вступает в силу с момента ее
                        размещения на Сайте, если иное не предусмотрено новой редакцией Политики
                        конфиденциальности.
                    </p>
                    <p>
                        10.3. Действующая Политика конфиденциальности размещена на странице по
                        адресу{" "}
                        <a
                            className={classes["privacy-policy__link"]}
                            href="https://likvi.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            https://likvi.com/
                        </a>
                        .
                    </p>
                </div>
            </Container>
        </Template>
    );
};

export default PrivacyPolicy;